import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Database, TreeDeciduous, Trash2, Eye, EyeOff, LogOut, Shield, Key, UserCircle, ChevronDown, Upload, FileText, Factory, Tags, Server, Brain, Settings, ScrollText } from 'lucide-react';
import { useIndexedDB } from '../hooks/useIndexedDB';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { useState, useEffect, useCallback, useRef } from 'react';
import SettingsDropdown from './SettingsDropdown';

const PREVIEW_KEY = 'banyan_preview_mode';

const ROLE_LABELS = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    user: 'User',
};

const ROLE_COLORS = {
    super_admin: 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 text-purple-700',
    admin: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-700',
    user: 'bg-gradient-to-r from-slate-400/20 to-slate-500/20 text-slate-600',
};

// Navigation structure with grouped items and icons
const NAV_STRUCTURE = [
    // Primary nav (always visible)
    { to: '/', label: 'Upload', menuKey: 'upload', icon: Upload },
    { to: '/results', label: 'Results', menuKey: 'results', icon: FileText },
    // Data Management submenu
    {
        label: 'Data Center',
        icon: Database,
        menuKey: '_data_mgmt',
        children: [
            { to: '/source-data', label: 'Source Data', menuKey: 'source_data', icon: Database, desc: 'View completed records saved to DB' },
            { to: '/manufacturer-vendor', label: 'Mfr & Vendor', menuKey: 'manufacturer_vendor', icon: Factory, desc: 'Manage manufacturer & vendor URLs' },
            { to: '/noun-modifier', label: 'Noun/Modifier', menuKey: 'noun_modifier', icon: Tags, desc: 'Configure noun & modifier mappings' },
            { to: '/property-conditions', label: 'Property Conditions', menuKey: 'property_conditions', icon: ScrollText, desc: 'Manage AI extraction rules per property' },
            { to: '/backend-bu', label: 'Backend BU', menuKey: 'backend_bu', icon: Server, desc: 'Backend business unit settings' },
        ]
    },
    // System
    { to: '/training', label: 'AI Training', menuKey: 'training', icon: Brain },
    { to: '/admin', label: 'Admin', menuKey: 'admin', icon: Shield },
];


function TopBar() {
    const { formattedDbSize, clearAll, refreshSize, itemCount } = useIndexedDB();
    const { user, logout, hasPermission, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showClearModal, setShowClearModal] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState(null);
    const userMenuRef = useRef(null);
    const submenuRef = useRef(null);

    // Preview toggle state (persisted in localStorage)
    const [previewOn, setPreviewOn] = useState(() => {
        const stored = localStorage.getItem(PREVIEW_KEY);
        return stored === null ? true : stored === 'true';
    });

    const togglePreview = useCallback(() => {
        setPreviewOn(prev => {
            const next = !prev;
            localStorage.setItem(PREVIEW_KEY, String(next));
            window.dispatchEvent(new CustomEvent('previewModeChanged', { detail: next }));
            toast.info(next ? '👁 Preview ON — manual approval required' : '⚡ Preview OFF — auto-run mode');
            return next;
        });
    }, []);

    // Close menus on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setShowUserMenu(false);
            }
            if (submenuRef.current && !submenuRef.current.contains(e.target)) {
                setOpenSubmenu(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Close submenu on route change
    useEffect(() => {
        setOpenSubmenu(null);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
        toast.success('Logged out successfully');
    };

    const handleClearDatabase = async () => {
        setShowClearModal(true);
    };

    const confirmClear = async () => {
        try {
            await clearAll();
            toast.success('IndexedDB cleared successfully');
            setShowClearModal(false);
            setTimeout(async () => {
                await refreshSize();
                window.location.reload();
            }, 500);
        } catch (error) {
            console.error('Error clearing IndexedDB:', error);
            toast.error('Failed to clear IndexedDB');
        }
    };

    // Check if a submenu group has an active child
    const isSubmenuActive = (children) => {
        return children?.some(child => {
            if (child.to === '/') return location.pathname === '/';
            return location.pathname.startsWith(child.to);
        });
    };

    // Check if a single nav item is active
    const isItemActive = (to) => {
        if (to === '/') return location.pathname === '/';
        return location.pathname.startsWith(to);
    };

    // Neumorphic style helpers
    const neuRaised = {
        background: 'var(--neu-bg)',
        boxShadow: '4px 4px 10px 0 rgba(163,177,198,0.5), -4px -4px 10px 0 rgba(255,255,255,0.7)',
    };

    const neuInset = {
        background: 'var(--neu-bg)',
        boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.45), inset -2px -2px 5px rgba(255,255,255,0.6)',
    };

    const neuActive = {
        background: '#0f6cbd',
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    };

    const neuFloat = {
        background: 'var(--neu-bg)',
        boxShadow: '8px 8px 18px 0 rgba(163,177,198,0.55), -8px -8px 18px 0 rgba(255,255,255,0.9), 0 4px 24px rgba(0,0,0,0.06)',
        animation: 'neuPop 0.25s cubic-bezier(0.34,1.56,0.64,1)',
    };

    // Render a single nav item or a submenu group
    const renderNavItem = (item, index) => {
        if (item.children) {
            // Submenu group — check if any child has permission
            const visibleChildren = item.children.filter(child => hasPermission(child.menuKey));
            if (visibleChildren.length === 0) return null;

            const groupActive = isSubmenuActive(visibleChildren);
            const isOpen = openSubmenu === item.menuKey;

            return (
                <div key={item.menuKey} className="relative" ref={isOpen ? submenuRef : undefined}>
                    <button
                        onClick={() => setOpenSubmenu(isOpen ? null : item.menuKey)}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            groupActive ? 'text-white' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        style={groupActive ? neuActive : {}}
                    >
                        <item.icon className="w-3.5 h-3.5" />
                        <span>{item.label}</span>
                        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isOpen && (
                        <div
                            className="absolute left-0 top-full mt-2 w-64 py-2 rounded-2xl z-50"
                            style={neuFloat}
                        >
                            {visibleChildren.map(child => {
                                const active = isItemActive(child.to);
                                return (
                                    <NavLink
                                        key={child.to}
                                        to={child.to}
                                        className="flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl transition-all duration-200 group"
                                        style={active ? {
                                            background: '#eff6fc',
                                            boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.3), inset -2px -2px 5px rgba(255,255,255,0.5)',
                                        } : {}}
                                        onClick={() => setOpenSubmenu(null)}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                            style={active ? {
                                                background: '#0f6cbd',
                                                boxShadow: '2px 2px 5px rgba(163,177,198,0.3), -2px -2px 5px rgba(255,255,255,0.4)',
                                            } : {
                                                ...neuInset,
                                            }}
                                        >
                                            <child.icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-semibold ${active ? 'text-teal-700' : 'text-slate-700 group-hover:text-slate-800'}`}>
                                                {child.label}
                                            </p>
                                            {child.desc && (
                                                <p className="text-[11px] text-slate-400 mt-0.5 truncate">{child.desc}</p>
                                            )}
                                        </div>
                                        {active && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Single nav item
        if (!hasPermission(item.menuKey)) return null;

        return (
            <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${isActive
                        ? 'text-white'
                        : 'text-slate-500 hover:text-slate-700'
                    }`
                }
                style={({ isActive }) => isActive ? neuActive : {}}
            >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
            </NavLink>
        );
    };

    return (
        <header className="sticky top-0 z-40" style={{
            background: 'var(--neu-bg)',
            boxShadow: '0 4px 14px 0 rgba(163,177,198,0.4), 0 -2px 10px 0 rgba(255,255,255,0.6)',
        }}>
            <div className="container mx-auto px-4 md:px-6">
                {/* Single Row Layout */}
                <div className="flex items-center h-16 gap-6">

                    {/* Logo — compact */}
                    <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
                        <div className="h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105" style={{
                            background: '#0f6cbd',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
                        }}>
                            <TreeDeciduous className="w-5 h-5 text-white" />
                        </div>
                        <div className="hidden lg:block">
                            <h1 className="text-base font-extrabold text-slate-800 tracking-tight leading-tight">Banyan AI</h1>
                            <p className="text-[10px] text-slate-400 font-medium leading-tight">Product Data Automation</p>
                        </div>
                    </Link>

                    {/* Divider */}
                    <div className="w-px h-8 rounded-full" style={{
                        background: 'linear-gradient(180deg, transparent, rgba(163,177,198,0.3), transparent)',
                    }} />

                    {/* Navigation — center */}
                    <nav className="flex items-center gap-1 p-1 rounded-2xl flex-1" style={neuInset}>
                        {NAV_STRUCTURE.map((item, i) => renderNavItem(item, i))}
                    </nav>

                    {/* Divider */}
                    <div className="w-px h-8 rounded-full" style={{
                        background: 'linear-gradient(180deg, transparent, rgba(163,177,198,0.3), transparent)',
                    }} />

                    {/* Right Toolbar — compact icon buttons */}
                    <div className="flex items-center gap-2 shrink-0">

                        {/* Preview Toggle */}
                        {hasPermission('preview_toggle') && (
                            <button
                                onClick={togglePreview}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200"
                                title={previewOn ? 'Preview is ON — click to auto-run' : 'Preview is OFF — click to enable manual approval'}
                                style={previewOn ? {
                                    ...neuInset,
                                    color: '#059669',
                                } : {
                                    ...neuRaised,
                                    color: '#d97706',
                                }}
                            >
                                {previewOn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                <span className="hidden xl:inline">{previewOn ? 'Preview' : 'Auto'}</span>
                            </button>
                        )}

                        {/* AI Settings */}
                        {hasPermission('ai_settings') && <SettingsDropdown />}

                        {/* DB Info + View Data + Clear — grouped */}
                        {hasPermission('view_data') && (
                            <div className="flex items-center gap-1 px-1 py-1 rounded-xl" style={neuInset}>
                                <div className="flex items-center gap-1 px-2 py-1.5">
                                    <Database className="w-3 h-3 text-slate-400" />
                                    <span className="text-xs font-bold text-slate-500">{itemCount}</span>
                                </div>
                                {hasPermission('clear_data') && (
                                    <button
                                        onClick={handleClearDatabase}
                                        className="p-1.5 rounded-lg text-red-400 hover:text-red-600 transition-all duration-200"
                                        title="Clear IndexedDB"
                                        style={neuRaised}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* User Menu */}
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="inline-flex items-center gap-2 px-2.5 py-2 rounded-xl transition-all duration-200"
                                style={showUserMenu ? neuInset : neuRaised}
                            >
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{
                                    background: '#0f6cbd',
                                    boxShadow: '2px 2px 4px rgba(163,177,198,0.3), -2px -2px 4px rgba(255,255,255,0.4)',
                                }}>
                                    <UserCircle className="w-4 h-4 text-white" />
                                </div>
                                <span className="text-sm font-bold text-slate-700 hidden lg:inline max-w-[100px] truncate">
                                    {user?.full_name || user?.username || 'User'}
                                </span>
                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
                            </button>

                            {showUserMenu && (
                                <div className="absolute right-0 mt-3 w-60 py-3 z-50 rounded-2xl" style={neuFloat}>
                                    {/* User Info */}
                                    <div className="px-4 py-3 border-b border-slate-200/40">
                                        <p className="font-bold text-slate-800 text-sm">{user?.full_name || user?.username}</p>
                                        <p className="text-xs text-slate-500">@{user?.username}</p>
                                        <span className={`inline-block mt-2 px-2.5 py-1 rounded-lg text-xs font-bold ${ROLE_COLORS[user?.role] || ROLE_COLORS.user}`} style={{
                                            boxShadow: 'inset 1px 1px 3px rgba(163,177,198,0.3), inset -1px -1px 3px rgba(255,255,255,0.5)',
                                        }}>
                                            {ROLE_LABELS[user?.role] || 'User'}
                                        </span>
                                    </div>

                                    {/* Menu Items */}
                                    {hasPermission('admin') && (
                                        <button
                                            onClick={() => { setShowUserMenu(false); navigate('/admin'); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 transition-all duration-150 hover:pl-5"
                                        >
                                            <Shield className="w-4 h-4 text-slate-400" />
                                            Admin Panel
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setShowUserMenu(false); navigate('/admin'); }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 transition-all duration-150 hover:pl-5"
                                    >
                                        <Key className="w-4 h-4 text-slate-400" />
                                        Change Password
                                    </button>

                                    <div className="border-t border-slate-200/40 mt-1 pt-1 mx-3">
                                        <button
                                            onClick={() => { setShowUserMenu(false); handleLogout(); }}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-red-500 hover:text-red-600 rounded-xl transition-all duration-150"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Clear Confirmation Modal */}
            {showClearModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{
                    background: 'rgba(224,229,236,0.75)',
                    backdropFilter: 'blur(8px)',
                }}>
                    <div className="max-w-md w-full mx-4 p-7 rounded-2xl" style={{
                        background: 'var(--neu-bg)',
                        boxShadow: '10px 10px 20px 0 rgba(163,177,198,0.7), -10px -10px 20px 0 rgba(255,255,255,0.85)',
                        animation: 'neuPop 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                    }}>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Clear All Data?</h3>
                        <p className="text-slate-500 mb-6">
                            Are you sure you want to clear all data from IndexedDB? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowClearModal(false)}
                                className="btn-secondary flex-1"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmClear}
                                className="flex-1 px-4 py-2.5 rounded-2xl font-semibold text-white transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                    boxShadow: '4px 4px 10px 0 rgba(163,177,198,0.5), -4px -4px 10px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(239,68,68,0.25)',
                                }}
                            >
                                Clear Data
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export default TopBar;
